import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Switch,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
} from '@mui/material';
import { Clock, Plus, Play, Pause, Code, Eye } from 'lucide-react';
import { API_URL } from '../globals';
import { supabase } from '../supabaseClient';
import axios from 'axios';

const CronJobsTab = ({ user }) => {
  const [projects, setProjects] = useState([]);
  const [cronjobs, setCronjobs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showCurlModal, setShowCurlModal] = useState(false);
  const [selectedProject, setSelectedProject] = useState('');
  const [schedule, setSchedule] = useState('0 3,15 * * *');
  const [metadata, setMetadata] = useState('{}');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [curlCommand, setCurlCommand] = useState('');
  const [updatingJobId, setUpdatingJobId] = useState(null);
  const [isRefreshingCronjobs, setIsRefreshingCronjobs] = useState(false);

  const commonSchedules = [
    { value: '0 3,15 * * *', label: 'Daily at 3 AM & 3 PM (Default)' },
    { value: '0 9 * * 1-5', label: 'Weekdays at 9 AM' },
    { value: '0 0 * * *', label: 'Daily at midnight' },
    { value: '0 */2 * * *', label: 'Every 2 hours' },
    { value: '0 0 * * 0', label: 'Weekly (Sundays at midnight)' },
    { value: '0 0 1 * *', label: 'Monthly (1st day at midnight)' },
  ];

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      await Promise.all([fetchProjects(), fetchCronjobs()]);
    } catch (error) {
      console.error('Error fetching data:', error);
      setError('Failed to load data');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchProjects = async () => {
    try {
      const authToken = (await supabase.auth.getSession()).data.session.access_token;
      const response = await axios.get(`${API_URL}/cronjob/projects`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      setProjects(response.data.projects || []);
    } catch (error) {
      console.error('Error fetching projects:', error);
      throw error;
    }
  };

  const fetchCronjobs = async (showSpinner = false) => {
    if (showSpinner) {
      setIsRefreshingCronjobs(true);
    }
    try {
      const authToken = (await supabase.auth.getSession()).data.session.access_token;
      const response = await axios.get(`${API_URL}/cronjob/list`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      setCronjobs(response.data.cronjobs || []);
    } catch (error) {
      console.error('Error fetching cronjobs:', error);
      throw error;
    } finally {
      if (showSpinner) {
        setIsRefreshingCronjobs(false);
      }
    }
  };

  const handleCreateCronjob = async () => {
    if (!selectedProject) {
      setError('Please select a project');
      return;
    }

    let parsedMetadata = {};
    try {
      if (metadata.trim()) {
        parsedMetadata = JSON.parse(metadata);
      }
    } catch (e) {
      setError('Invalid JSON in metadata field');
      return;
    }

    setIsCreating(true);
    setError('');
    setSuccess('');

    try {
      const authToken = (await supabase.auth.getSession()).data.session.access_token;
      const response = await axios.post(
        `${API_URL}/cronjob/create`,
        {
          playwright_project_id: parseInt(selectedProject),
          schedule,
          metadata: parsedMetadata,
        },
        { headers: { Authorization: `Bearer ${authToken}` } }
      );

      setSuccess('Cronjob created successfully!');
      setShowCreateModal(false);
      setSelectedProject('');
      setSchedule('0 3,15 * * *');
      setMetadata('{}');
      fetchCronjobs(true);
    } catch (error) {
      console.error('Error creating cronjob:', error);
      setError(error.response?.data?.error || 'Failed to create cronjob');
    } finally {
      setIsCreating(false);
    }
  };

  const handleToggleCronjob = async (cronjobId, isActive) => {
    setUpdatingJobId(cronjobId);
    try {
      const authToken = (await supabase.auth.getSession()).data.session.access_token;
      const endpoint = isActive ? 'disable' : 'enable';
      
      await axios.put(
        `${API_URL}/cronjob/${endpoint}/${cronjobId}`,
        {},
        { headers: { Authorization: `Bearer ${authToken}` } }
      );

      setSuccess(`Cronjob ${isActive ? 'disabled' : 'enabled'} successfully`);
      fetchCronjobs(true);
    } catch (error) {
      console.error('Error toggling cronjob:', error);
      setError(error.response?.data?.error || `Failed to ${isActive ? 'disable' : 'enable'} cronjob`);
    } finally {
      setUpdatingJobId(null);
    }
  };

  const handleGenerateCurl = async (projectId) => {
    try {
      const authToken = (await supabase.auth.getSession()).data.session.access_token;
      const response = await axios.get(
        `${API_URL}/cronjob/test-curl?playwright_project_id=${projectId}`,
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      
      setCurlCommand(response.data.curl_command);
      setShowCurlModal(true);
    } catch (error) {
      console.error('Error generating curl command:', error);
      setError(error.response?.data?.error || 'Failed to generate curl command');
    }
  };

  const getProjectName = (comment) => {
    // Extract project info from comment like "playwright-project-123-tests"
    const match = comment?.match(/playwright-project-(\d+)-tests/);
    if (match) {
      const projectId = parseInt(match[1]);
      const project = projects.find(p => p.id === projectId);
      return project ? project.repo_slug || `Project ${projectId}` : `Project ${projectId}`;
    }
    return 'Unknown Project';
  };

  const formatScheduleDescription = (schedule) => {
    const scheduleMap = {
      '0 3,15 * * *': 'Daily at 3 AM & 3 PM',
      '0 9 * * 1-5': 'Weekdays at 9 AM',
      '0 0 * * *': 'Daily at midnight',
      '0 */2 * * *': 'Every 2 hours',
      '0 0 * * 0': 'Weekly (Sundays)',
      '0 0 1 * *': 'Monthly (1st day)',
    };
    return scheduleMap[schedule] || schedule;
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
        <CircularProgress sx={{ color: '#fff' }} />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ color: '#fff', fontWeight: 600, mb: 1 }}>
          Cron Jobs
        </Typography>
        <Typography sx={{ color: '#888', mb: 3 }}>
          Schedule automated Playwright tests to run at specific times
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 3, backgroundColor: '#ff52521a', color: '#ff5252' }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 3, backgroundColor: '#4caf501a', color: '#4caf50' }}>
            {success}
          </Alert>
        )}

        <Button
          variant="contained"
          startIcon={<Plus size={18} />}
          onClick={() => setShowCreateModal(true)}
          sx={{
            backgroundColor: '#fff',
            color: '#000',
            '&:hover': { backgroundColor: '#f0f0f0' },
            textTransform: 'none',
            fontWeight: 600,
          }}
        >
          Create Cron Job
        </Button>
      </Box>

      {/* Cronjobs Table */}
      <Card sx={{ backgroundColor: '#1a1a1a', border: '1px solid #2a2a2a' }}>
        <CardContent>
          <Typography variant="h6" sx={{ color: '#fff', mb: 2 }}>
            Active Cron Jobs
          </Typography>
          
          {isRefreshingCronjobs ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
              <CircularProgress sx={{ color: '#fff' }} />
            </Box>
          ) : cronjobs.length === 0 ? (
            <Typography sx={{ color: '#888' }}>
              No cron jobs configured yet. Create your first cron job to automate your tests.
            </Typography>
          ) : (
            <TableContainer component={Paper} sx={{ backgroundColor: 'transparent' }}>
              <Table sx={{ '& .MuiTableCell-root': { borderColor: '#2a2a2a' } }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ color: '#fff', fontWeight: 600 }}>Project</TableCell>
                    <TableCell sx={{ color: '#fff', fontWeight: 600 }}>Schedule</TableCell>
                    <TableCell sx={{ color: '#fff', fontWeight: 600 }}>Status</TableCell>
                    <TableCell sx={{ color: '#fff', fontWeight: 600 }}>Created</TableCell>
                    <TableCell sx={{ color: '#fff', fontWeight: 600 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {cronjobs.map((job) => (
                    <TableRow key={job.id}>
                      <TableCell sx={{ color: '#fff' }}>
                        {getProjectName(job.comment)}
                      </TableCell>
                      <TableCell sx={{ color: '#888' }}>
                        <Box>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                            {job.schedule}
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#666' }}>
                            {formatScheduleDescription(job.schedule)}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={job.active ? 'Active' : 'Inactive'}
                          color={job.active ? 'success' : 'default'}
                          size="small"
                          sx={{
                            backgroundColor: job.active ? '#4caf501a' : '#6661',
                            color: job.active ? '#4caf50' : '#888',
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ color: '#888' }}>
                        {new Date(job.created_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Tooltip title={job.active ? 'Disable cron job' : 'Enable cron job'}>
                            <IconButton
                              size="small"
                              onClick={() => handleToggleCronjob(job.id, job.active)}
                              disabled={updatingJobId === job.id}
                              sx={{ color: job.active ? '#ff5252' : '#4caf50' }}
                            >
                              {updatingJobId === job.id ? (
                                <CircularProgress size={16} />
                              ) : job.active ? (
                                <Pause size={16} />
                              ) : (
                                <Play size={16} />
                              )}
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Create Cronjob Modal */}
      <Dialog
        open={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: '#1a1a1a',
            border: '1px solid #2a2a2a',
            color: '#fff',
          },
        }}
      >
        <DialogTitle sx={{ color: '#fff' }}>Create New Cron Job</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 2 }}>
            <FormControl fullWidth>
              <InputLabel sx={{ color: '#888' }}>Project</InputLabel>
              <Select
                value={selectedProject}
                onChange={(e) => setSelectedProject(e.target.value)}
                sx={{
                  color: '#fff',
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#333' },
                  '& .MuiSvgIcon-root': { color: '#888' },
                }}
              >
                {projects.map((project) => (
                  <MenuItem key={project.id} value={project.id}>
                    {project.repo_slug || `Project ${project.id}`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel sx={{ color: '#888' }}>Schedule</InputLabel>
              <Select
                value={schedule}
                onChange={(e) => setSchedule(e.target.value)}
                sx={{
                  color: '#fff',
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#333' },
                  '& .MuiSvgIcon-root': { color: '#888' },
                }}
              >
                {commonSchedules.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="Custom Schedule (Cron Format)"
              value={schedule}
              onChange={(e) => setSchedule(e.target.value)}
              placeholder="0 3,15 * * *"
              helperText="Format: minute hour day month dayOfWeek"
              sx={{
                '& .MuiInputBase-input': { color: '#fff' },
                '& .MuiInputLabel-root': { color: '#888' },
                '& .MuiOutlinedInput-notchedOutline': { borderColor: '#333' },
                '& .MuiFormHelperText-root': { color: '#666' },
              }}
            />

            <TextField
              label="Metadata (JSON)"
              value={metadata}
              onChange={(e) => setMetadata(e.target.value)}
              multiline
              rows={3}
              placeholder='{"env": "production", "branch": "main"}'
              helperText="Optional metadata to pass to the test trigger"
              sx={{
                '& .MuiInputBase-input': { color: '#fff', fontFamily: 'monospace' },
                '& .MuiInputLabel-root': { color: '#888' },
                '& .MuiOutlinedInput-notchedOutline': { borderColor: '#333' },
                '& .MuiFormHelperText-root': { color: '#666' },
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button
            onClick={() => setShowCreateModal(false)}
            sx={{ color: '#888' }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleCreateCronjob}
            disabled={isCreating}
            variant="contained"
            sx={{
              backgroundColor: '#fff',
              color: '#000',
              '&:hover': { backgroundColor: '#f0f0f0' },
            }}
          >
            {isCreating ? <CircularProgress size={20} /> : 'Create Cron Job'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Curl Command Modal */}
      <Dialog
        open={showCurlModal}
        onClose={() => setShowCurlModal(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: '#1a1a1a',
            border: '1px solid #2a2a2a',
            color: '#fff',
          },
        }}
      >
        <DialogTitle sx={{ color: '#fff' }}>Test Curl Command</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: '#888', mb: 2 }}>
            Use this curl command to manually trigger tests for this project:
          </Typography>
          <Box
            sx={{
              backgroundColor: '#0f0f0f',
              border: '1px solid #333',
              borderRadius: 1,
              p: 2,
              fontFamily: 'monospace',
              fontSize: '0.875rem',
              color: '#fff',
              overflow: 'auto',
              whiteSpace: 'pre-wrap',
            }}
          >
            {curlCommand}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button
            onClick={() => navigator.clipboard.writeText(curlCommand)}
            variant="outlined"
            sx={{
              borderColor: '#333',
              color: '#fff',
              '&:hover': { borderColor: '#555' },
            }}
          >
            Copy to Clipboard
          </Button>
          <Button
            onClick={() => setShowCurlModal(false)}
            variant="contained"
            sx={{
              backgroundColor: '#fff',
              color: '#000',
              '&:hover': { backgroundColor: '#f0f0f0' },
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CronJobsTab; 